import { LazyQueryResultTuple, QueryResult } from '@apollo/client';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useLocalize, useWorkspace } from '@treams/treams-hooks';
import { AddIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { PageType } from '@treams/treams-network';
import {
	Button,
	Card,
	ChevronRightIcon,
	Container,
	DraggableCard,
	FlexGap,
	Heading3,
	Jumbo,
	PAGE_CONTENT_WIDTH,
	PencilEditBoxIcon,
	Reorder,
	ReorderRenderState,
	Separator,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import GoalsList, { GoalsQuery } from './GoalsList';
import GoalsTabs from './GoalsTabs';

function GoalDriverCard(props: {
	driver: graphql.GoalDriver;
	draggable?: boolean;
	dragHandleProps?: ReorderRenderState['dragHandleProps'];
	isDragging?: boolean;
	loading?: boolean;
}) {
	const { localize } = useLocalize();

	return (
		<DraggableCard
			dragHandleProps={props.dragHandleProps ?? undefined}
			isDragging={props.isDragging}
			loading={props.loading}
			isDragDisabled={!props.draggable}
		>
			<Heading3>{localize(props.driver.title)?.text}</Heading3>
		</DraggableCard>
	);
}

GoalDriverCard.Skeleton = function GoalDriverCardSkeleton() {
	return (
		<Card.Skeleton p={`${tokens.padding.container}px`}>
			<Heading3.Skeleton />
		</Card.Skeleton>
	);
};

function GoalDrivers() {
	const { t } = useTranslation();

	const query = graphql.useGoalDriversQuery({
		variables: { isArchived: false },
		fetchPolicy: 'network-only',
	});

	const [update] = graphql.useReorderGoalDriverMutation();
	const [orders, setOrders] = useState<Record<string, number>>({});
	const goalDrivers = query.data?.goalDrivers ?? [];
	const sortedDrivers = _.sortBy(
		goalDrivers,
		(driver) => -(orders[driver.id] ?? driver.order ?? 0),
	);

	function order(index: number) {
		const driver = sortedDrivers[index];

		if (!driver) {
			return Date.now();
		}

		return orders[driver.id] ?? driver.order ?? 0;
	}

	const canReorder = query.data?.viewer.workspace.viewerCanCreateGoalDrivers ?? false;

	return (
		<Box>
			<Flex alignItems="center" mb={`${tokens.gap.list}px`}>
				<Heading3 flexGrow={1} flexShrink={1}>
					{t('goals.collective.goalDrivers')}
				</Heading3>
				{canReorder && (
					<Button
						ml={2}
						icon={<PencilEditBoxIcon />}
						rightIcon={<ChevronRightIcon />}
						title={t('goals.index.toolbar.drivers')}
						link={`/settings/organization/drivers`}
						type="secondary"
						small
					/>
				)}
			</Flex>
			{query.data ? (
				canReorder ? (
					<Reorder.Container
						onReorder={async (id, index) => {
							const current = sortedDrivers.findIndex((driver) => driver.id === id);
							const shift = current > index ? -1 : 0;

							const previous = order(index + shift);
							const next = order(index + shift + 1);
							const mean =
								index === sortedDrivers.length - 1
									? order(index + shift) / 2
									: (next - previous) / 2 + previous;

							setOrders({ ...orders, [id]: mean });

							await update({ variables: { input: { id, order: mean } } });

							setOrders(_.omit(orders, id));
						}}
					>
						{sortedDrivers.map((driver, index) => (
							<Reorder
								key={driver.id}
								id={driver.id}
								index={index}
								loading={driver.id in orders}
								mb={`${tokens.gap.list}px`}
								render={({ dragHandleProps, isDragging }) => (
									<GoalDriverCard
										driver={driver as graphql.GoalDriver}
										draggable
										dragHandleProps={dragHandleProps}
										isDragging={isDragging}
										loading={driver.id in orders}
									/>
								)}
							/>
						))}
					</Reorder.Container>
				) : (
					<FlexGap flexDirection="column" gap={tokens.gap.list}>
						{sortedDrivers.map((driver) => (
							<GoalDriverCard key={driver.id} driver={driver as graphql.GoalDriver} />
						))}
					</FlexGap>
				)
			) : (
				<FlexGap flexDirection="column" gap={tokens.gap.list}>
					<Skeleton.Repeat count={3}>
						<GoalDriverCard.Skeleton />
					</Skeleton.Repeat>
				</FlexGap>
			)}
		</Box>
	);
}

export function OrganizationGoalsList(props: {
	selectedGoal?: graphql.Goal;
	onSelect?: (goal: graphql.Goal) => void;
	hideArchived?: boolean;
}) {
	const { t } = useTranslation();

	const workspace = useWorkspace();

	const query = graphql.useWorkspaceGoalsQuery({
		variables: { id: workspace.id, status: graphql.GoalStatus.Active, last: 100 },
		fetchPolicy: 'network-only',
	});
	const completedQuery = graphql.useWorkspaceGoalsQuery({
		variables: { id: workspace.id, status: graphql.GoalStatus.Completed, last: 100 },
		fetchPolicy: 'network-only',
	});
	const archivedQuery = graphql.useWorkspaceGoalsLazyQuery({
		variables: { id: workspace.id, status: graphql.GoalStatus.Archived, last: 100 },
		fetchPolicy: 'network-only',
	});

	return (
		<Box>
			<Flex alignItems="center" mb={`${tokens.gap.list}px`}>
				<Heading3 flexGrow={1} flexShrink={1}>
					{t('goals.collective.organizationGoals')}
				</Heading3>
				{query.data?.workspace.viewerCanCreateGoal && !props.onSelect && (
					<Button
						ml={2}
						icon={<AddIcon />}
						title={t('goals.index.toolbar.new')}
						link={`/goals/new?workspace_id=${workspace.id}`}
						type="secondary"
						small
					/>
				)}
			</Flex>
			<GoalsList
				query={query as QueryResult<GoalsQuery>}
				path="workspace.goalsV2"
				completedQuery={completedQuery as QueryResult<GoalsQuery>}
				archivedQuery={
					query.data?.workspace.viewerCanViewArchivedGoals && !props.hideArchived
						? (archivedQuery as LazyQueryResultTuple<GoalsQuery, object>)
						: undefined
				}
				user={undefined}
				onSelect={props.onSelect}
				selectedGoal={props.selectedGoal}
				reorderable={query.data?.workspace.viewerCanCreateGoal}
				contained={false}
			/>
		</Box>
	);
}

export default function OrganizationGoals() {
	const { t } = useTranslation();

	return (
		<Box>
			<Jumbo.Editable
				hasMediaUpload
				page={PageType.GoalsOrganization}
				title={t('goals.index.title')}
				description={t('goals.index.description')}
				footer={<GoalsTabs />}
			/>
			<Container mt={`${tokens.gap.page}px`} mb={4} wide={PAGE_CONTENT_WIDTH}>
				<GoalDrivers />
				<Separator my={`${tokens.gap.section}px`} />
				<OrganizationGoalsList />
			</Container>
		</Box>
	);
}
